import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function TermsPage() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="terms-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-emerald-400 transition-colors mb-8" data-testid="terms-back-link">
          <ArrowLeft size={14} /> Back to home
        </Link>

        <article className="prose prose-invert prose-slate max-w-none
          prose-headings:text-foreground prose-headings:font-bold
          prose-h1:text-3xl sm:prose-h1:text-4xl prose-h1:mb-2
          prose-h2:text-xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:border-b prose-h2:border-border prose-h2:pb-3
          prose-p:text-muted-foreground prose-p:leading-relaxed
          prose-li:text-muted-foreground prose-li:leading-relaxed
          prose-strong:text-foreground
          prose-a:text-emerald-400 prose-a:no-underline hover:prose-a:text-emerald-300
          prose-hr:border-border">

          <h1 data-testid="terms-title">Agent9 Terms of Service</h1>
          <p className="text-sm text-muted-foreground !mt-1 !mb-8"><strong className="text-muted-foreground">Effective Date:</strong> March 18, 2026</p>
          <p>Welcome to Agent9 ("Agent9", "we", "our", or "us"). These Terms of Service ("Terms") govern your use of the Agent9 platform.</p>
          <p>By accessing or using Agent9, you agree to these Terms.</p>

          <hr />

          <h2>1. Description of Service</h2>
          <p>Agent9 is a software platform that provides AI-powered communication tools for real estate professionals, including call handling, messaging, automation, and lead management.</p>
          <p>Agent9 is <strong>not a licensed real estate broker, agent, or legal advisor</strong>.</p>

          <h2>2. User Responsibilities</h2>
          <p>You agree to:</p>
          <ul>
            <li>Use Agent9 in compliance with all applicable laws and regulations</li>
            <li>Ensure all communications comply with real estate licensing laws</li>
            <li>Provide accurate account and business information</li>
            <li>Supervise AI-generated interactions conducted on your behalf</li>
          </ul>
          <p>You are solely responsible for:</p>
          <ul>
            <li>Your leads, clients, and communications</li>
            <li>Compliance with state and federal real estate regulations</li>
            <li>Ensuring proper disclosures to consumers</li>
          </ul>

          <h2>3. AI Disclaimer</h2>
          <p>Agent9 uses artificial intelligence to generate responses and automate communications.</p>
          <p>You acknowledge:</p>
          <ul>
            <li>AI responses may not always be accurate or complete</li>
            <li>Agent9 does not provide legal, financial, or contractual advice</li>
            <li>You must verify important information before relying on it</li>
          </ul>

          <h2>4. Communication &amp; Consent</h2>
          <p>By using Agent9, you agree:</p>
          <ul>
            <li>You have obtained proper consent to contact leads via call, SMS, or email</li>
            <li>You will honor opt-out requests (e.g., STOP for SMS)</li>
            <li>You will comply with TCPA, CAN-SPAM, and applicable laws</li>
          </ul>

          <h2>5. Data &amp; Privacy</h2>
          <p>Your use of Agent9 is also governed by our <Link to="/privacy" data-testid="terms-privacy-link">Privacy Policy</Link>.</p>

          <h2>6. Subscription &amp; Billing</h2>
          <ul>
            <li>Agent9 operates on a subscription basis</li>
            <li>Fees are billed monthly or annually</li>
            <li>Overage charges may apply for usage beyond plan limits</li>
            <li>All fees are non-refundable unless otherwise stated</li>
          </ul>

          <h2>7. Service Availability</h2>
          <p>We strive to provide reliable service but do not guarantee:</p>
          <ul>
            <li>Uninterrupted availability</li>
            <li>Error-free operation</li>
          </ul>
          <p>We may update or modify features at any time.</p>

          <h2>8. Limitation of Liability</h2>
          <p>To the maximum extent permitted by law, Agent9 is not liable for:</p>
          <ul>
            <li>Lost deals, revenue, or business opportunities</li>
            <li>Errors in AI-generated responses</li>
            <li>User misuse of the platform</li>
          </ul>

          <h2>9. Termination</h2>
          <p>We may suspend or terminate accounts that:</p>
          <ul>
            <li>Violate these Terms</li>
            <li>Misuse the platform</li>
            <li>Create legal or operational risk</li>
          </ul>

          <h2>10. Governing Law</h2>
          <p>These Terms are governed by the laws of the State of California.</p>

          <h2>11. Contact</h2>
          <p>For questions, contact: <a href="mailto:support@agent9.ai">support@agent9.ai</a></p>
        </article>
      </div>
    </div>
  );
}
